import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA,
  MatLegacyDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, finalize, Subject, takeUntil } from 'rxjs';

// CONFIGS
import { MaterialCustomModule } from 'v2/material-custom.module';
import {
  dimensionUnits,
  weightUnits,
} from 'v2/modules/invoice/config/shipstation.config';

// COMPONENTS

// INTERFACES
import {
  EDimensionUnit,
  EWeightUnit,
} from 'v2/modules/invoice/interfaces/shipstation-shipment.interface';
import { FulfillmentOrderAutocompleteControlComponent } from 'v2/modules/shared/components/fulfillment-order-autocomplete-control/fulfillment-order-autocomplete-control.component';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';

// MODULES
import { DirectiveModule } from 'v2/modules/shared/directives/directive.module';
import { OptionInterface } from 'v2/modules/shared/interfaces/common.interface';
import {
  EShipmentFormDataSource,
  IManualShipmentPayload,
  IShipmentFormData,
  TShipmentFormDataSource,
} from 'v2/modules/shared/interfaces/order-shipment.interface';
import { PipeModule } from 'v2/modules/shared/pipes/pipe.module';

// SERVICES
import { ShipstationShipmentApiService } from 'v2/modules/shared/services/api/shipstation-shipment-api.service';

// UTILS
import {
  trackByLabel,
  trackByControlValue,
} from 'v2/modules/shared/util/track-by.util';
import { UiModule } from 'v2/modules/ui/ui.module';

import { IFulfillmentOrder } from '../../interfaces/order-fulfillment.interface';

@Component({
  selector: 'lum-order-shipments-form',
  templateUrl: './order-shipments-form.component.html',
  styleUrls: ['./order-shipments-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialCustomModule,
    UiModule,
    DirectiveModule,
    FulfillmentOrderAutocompleteControlComponent,
    PipeModule,
  ],
})
export class OrderShipmentsFormComponent implements OnInit {
  shipmentForm! :FormGroup;
  isEditing = false;
  weightUnits :OptionInterface[] = weightUnits;
  dimensionUnits :OptionInterface[] = dimensionUnits;

  loading$ = new BehaviorSubject<boolean>(true);
  submitting$ = new BehaviorSubject<boolean>(false);
  salesOrderId :number | null = null;
  dimensionUnit :string;
  weightUnit :string;
  hasIncludedItems :boolean = false;

  trackByLabel = trackByLabel;
  trackByControlValue = trackByControlValue;

  private onDestroy$ = new Subject<void>();

  constructor(
    private shipstationShipmentApiService :ShipstationShipmentApiService,
    private dialogRef :MatLegacyDialogRef<OrderShipmentsFormComponent>,
    private fb :FormBuilder,
    private snackBar :MatLegacySnackBar,
    @Inject(MAT_LEGACY_DIALOG_DATA)
    public data :{
      source :TShipmentFormDataSource;
      sourceId :number;
    },
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.initData();
    this.isEditing = this.data.source === EShipmentFormDataSource.SHIPMENT;

    if (this.data.source === EShipmentFormDataSource.SALES_ORDER) {
      this.salesOrderId = this.data.sourceId;
    }
  }

  onSubmit() :void {
    if (this.shipmentForm.invalid) {
      return;
    }

    this.submitting$.next(true);

    if (this.data.source === EShipmentFormDataSource.SHIPMENT) {
      this.shipstationShipmentApiService
        .updateManualShipment(this.data.sourceId, this.payload)
        .pipe(finalize(() => this.submitting$.next(false)))
        .subscribe({
          next: (response) => {
            this.snackBar.open('Shipment updated successfully', 'Close', {
              duration: SnackBarConfig.SUCCESS.duration,
            });
            this.dialogRef.close(true);
          },
          error: (error) => {
            this.snackBar.open(
              error?.error || 'There was an error updating the shipment',
              'Close',
              { duration: SnackBarConfig.ERROR.duration },
            );
          },
        });

      return;
    }

    this.shipstationShipmentApiService
      .createManualShipment(this.payload)
      .pipe(finalize(() => this.submitting$.next(false)))
      .subscribe({
        next: (response) => {
          this.snackBar.open('Shipment created successfully', 'Close', {
            duration: SnackBarConfig.SUCCESS.duration,
          });
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.snackBar.open(
            error?.error || 'There was an error creating the shipment',
            'Close',
            {
              duration: SnackBarConfig.ERROR.duration,
            },
          );
        },
      });
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onSetUnit(field :string, unit :string) :void {
    this.shipmentForm.get(field)?.patchValue(unit);
  }

  private initForm(shipmentFormData ?:IShipmentFormData) :void {
    this.shipmentForm = this.fb.group({
      fulfillmentOrder: shipmentFormData?.fulfillmentId
        ? { id: shipmentFormData?.fulfillmentId }
        : null,
      fulfillmentOrderId: [
        {
          value: shipmentFormData?.fulfillmentId || null,
          disabled: this.data.source !== EShipmentFormDataSource.SALES_ORDER,
        },
      ],
      warehouseGroup: [
        { value: shipmentFormData?.warehouseGroup || '', disabled: true },
        Validators.required,
      ],
      shipmentId: [
        {
          value: shipmentFormData?.shipmentId || '',
          disabled: this.data.source === EShipmentFormDataSource.SHIPMENT,
        },
        Validators.required,
      ],
      warehouseGroupId: [
        shipmentFormData?.warehouseGroupId || '',
        Validators.required,
      ],
      shipDate: [shipmentFormData?.shipDate || '', Validators.required],
      carrierCode: [shipmentFormData?.carrierCode || '', Validators.required],
      trackingNumber: [shipmentFormData?.trackingNumber || '', Validators.required],
      sscc: [shipmentFormData?.sscc || '', Validators.required],
      cost: [
        shipmentFormData?.cost || 0,
        [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$')],
      ],
      status: [shipmentFormData?.status || 'PENDING', Validators.required],
      weight: this.fb.group({
        units: [shipmentFormData?.weight?.units || EWeightUnit.POUNDS],
        value: [shipmentFormData?.weight?.value || 0, [Validators.required]],
      }),
      dimensions: this.fb.group({
        units: [shipmentFormData?.dimensions?.units || EDimensionUnit.INCHES],
        length: [shipmentFormData?.dimensions?.length || 0],
        width: [shipmentFormData?.dimensions?.width || 0],
        height: [shipmentFormData?.dimensions?.height || 0],
      }),

      // Items Section
      items: this.fb.array(
        shipmentFormData?.items.map((item) => {
          return this.fb.group({
            include: [
              {
                value: this.data.source === EShipmentFormDataSource.SHIPMENT,
                disabled:
                  this.data.source !== EShipmentFormDataSource.SHIPMENT &&
                  +item.remainingQty <= 0,
              },
            ],
            orderItemId: [item.orderItemId],
            sku: [{ value: item.sku, disabled: true }],
            productName: [{ value: item.productName, disabled: true }],
            quantity: [
              {
                value: item.quantity,
                disabled:
                  (this.isEditing && shipmentFormData?.status === 'SHIPPED') ||
                  +item.remainingQty <= 0,
              },
              [
                Validators.required,
                Validators.min(1),
                this.quantityValidator(+item.remainingQty),
              ],
            ],
            remainingQty: [item.remainingQty],
            availableQty: [item.availableQty],
            salesOrderItemId: [item.salesOrderItemId],
            fulfillmentOrderItemId: [item.fulfillmentOrderItemId],
          });
        }) || [],
      ),
    });

    this.dimensionUnit = this.computeDimensionUnit();
    this.weightUnit = this.computeWeightUnit();
    this.hasIncludedItems = this.computeHasIncludedItems();

    this.listenToFormChanges();
    this.shipmentForm.markAllAsTouched();
  }

  private initData(params ?:{
    source :TShipmentFormDataSource;
    sourceId :number;
  }) :void {
    let { source, sourceId } = this.data;

    if (params) {
      source = params.source;
      sourceId = params.sourceId;
    }

    this.loading$.next(true);
    this.shipstationShipmentApiService
      .getShipmentDataFromSource({
        source,
        sourceId,
      })
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this.initForm(response);
        },
        error: (error) => {
          this.snackBar.open('Error loading shipment data.', 'Close', {
            duration: SnackBarConfig.ERROR.duration,
          });
          this.loading$.next(false);
        },
      });
  }

  private quantityValidator(
    availableQty :number,
  ) :(control :AbstractControl) => ValidationErrors | null {
    return (control :AbstractControl) :ValidationErrors | null => {
      const quantity = control.value;

      return quantity > availableQty ? { exceedsAvailable: true } : null;
    };
  }

  private listenToFormChanges() :void {
    if (this.data.source === EShipmentFormDataSource.SALES_ORDER) {
      // Subscribe to fullfillmentOrderId changes
      this.shipmentForm
        .get('fulfillmentOrder')
        .valueChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe((selectedFulfillmentOrder :IFulfillmentOrder) => {
          if (!selectedFulfillmentOrder?.id) {
            return;
          }

          this.initData({
            source: EShipmentFormDataSource.FULFILLMENT_ORDER,
            sourceId: selectedFulfillmentOrder.id,
          });
        });
    }

    this.shipmentForm
      .get('dimensions.units')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.dimensionUnit = this.computeDimensionUnit();
      });

    this.shipmentForm
      .get('weight.units')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.weightUnit = this.computeWeightUnit();
      });

    this.shipmentForm
      .get('items')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.hasIncludedItems = this.computeHasIncludedItems();
      });
  }

  private computeDimensionUnit() :string {
    const unit = this.shipmentForm.get('dimensions.units')?.value;

    return this.dimensionUnits.find((obj) => obj.value === unit)?.label || '';
  }

  private computeWeightUnit() :string {
    const unit = this.shipmentForm.get('weight.units')?.value;

    return this.weightUnits.find((obj) => obj.value === unit)?.label || '';
  }

  private computeHasIncludedItems() :boolean {
    const items = this.shipmentForm.get('items')?.getRawValue() || [];

    return items.some((item :any) => item.include === true);
  }

  get payload() :IManualShipmentPayload {
    const {
      warehouseGroupId,
      shipmentId,
      shipDate,
      carrierCode,
      trackingNumber,
      sscc,
      cost,
      status,
      weight,
      dimensions,
      items,
      fulfillmentOrderId,
    } = this.shipmentForm.getRawValue();

    return {
      salesOrderId:
        this.data.source === EShipmentFormDataSource.SALES_ORDER
          ? this.data.sourceId
          : null,
      fulfillmentOrderId: fulfillmentOrderId ?? null,
      warehouseGroupId: warehouseGroupId,
      shipmentId,
      shipDate,
      carrierCode,
      trackingNumber,
      sscc,
      cost,
      status,
      weight,
      dimensions,
      items: items
        .filter((item) => item.include === true)
        .map((item) => ({
          orderItemId: item.orderItemId,
          quantity: item.quantity,
          salesOrderItemId: item.salesOrderItemId,
          fulfillmentOrderItemId: item.fulfillmentOrderItemId,
        })),
    } as IManualShipmentPayload;
  }
}
